import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CalendarDays, AlertTriangle, Info, Flame, Droplets } from "lucide-react";
import heroImage from "@/assets/gallery/tubing-canyon-view.jpg";
import inlineRiver from "@/assets/gallery/friends-tubing.jpg";
import inlineCabanas from "@/assets/gallery/aerial-river-cabanas.jpg";

const SLUG = "guadalupe-river-state-park-family-guide-2026";
const CANONICAL = `/blog/${SLUG}`;
const SITE = "https://sonsguadalupe.com";
const FULL_URL = `${SITE}${CANONICAL}`;
const TITLE = "Guadalupe River State Park Family Guide 2026";
const HEAD_TITLE =
  "Guadalupe River State Park: The 2026 Family Guide to Tubing, Swimming & Camping";
const DESCRIPTION =
  "Plan a Guadalupe River State Park trip with tubing, swimming, camping, rules, fees, reservations, river conditions, safety tips, and private river alternatives.";
const PUBLISH_DATE = "June 25, 2026";
const PUBLISH_ISO = "2026-06-25";
const HERO_ALT =
  "Family-friendly Guadalupe River day near Son's Guadalupe in New Braunfels Texas";

const FAQS: { q: string; a: string }[] = [
  {
    q: "What activities can families do at Guadalupe River State Park?",
    a: "Guadalupe River State Park offers swimming, wading, tubing, paddling, and fishing along roughly 4 miles of Guadalupe River frontage. The river's consistent 68–72°F temperature makes it a natural cool-down spot on hot Texas summer days. Shallow limestone shelves near the bank are great wading spots for younger kids, while longer floats on tubes or in kayaks and canoes suit older kids and adults. The park also has a campground for overnight stays. Keep in mind the park provides no rentals, no lifeguards, and no tube shuttle — you'll need to bring your own gear and manage your own logistics. Hikers can also explore the separate Bauer Unit, though it has no river access.",
  },
  {
    q: "Are there lifeguards at Guadalupe River State Park?",
    a: "No — Guadalupe River State Park does not station lifeguards anywhere along its river frontage. Water safety is entirely the family's responsibility. The riverbed can shift quickly from ankle-deep sandy shallows to deeper swimming holes cut by the current, sometimes within just a few feet of each other. Parents are advised to scout any entry point before letting young children run in. PFDs (life jackets) are strongly recommended for non-strong swimmers. If you'd prefer a setup with more onsite staff support and a managed environment, a private outfitter like Son's Guadalupe on River Road in New Braunfels is worth considering.",
  },
  {
    q: "Does Guadalupe River State Park charge an entry fee?",
    a: "Yes, a per-person day-use fee applies at Guadalupe River State Park. The article recommends confirming the current 2026 rates directly at tpwd.texas.gov, as fees are set by the Texas Parks & Wildlife Department (TPWD) and can change. Both day-use passes and campsites can be reserved in advance through the TPWD reservation system. When booking, pay close attention to which park unit your pass covers — day-use access is generally for the main unit on Park Road 31, not the hike-only Bauer Unit.",
  },
  {
    q: "Can I bring alcohol or glass containers to Guadalupe River State Park?",
    a: "Neither alcohol nor glass containers are permitted at Guadalupe River State Park. Alcohol is prohibited in public areas under Texas State Park rules, and glass containers are banned throughout the park. Plan your cooler accordingly — canned drinks and plastic bottles are your best options. These restrictions apply to the entire park, including river access points and picnic areas.",
  },
  {
    q: "How do I make a reservation for camping or day use at Guadalupe River State Park?",
    a: "Reservations for both day-use passes and campsites at Guadalupe River State Park are made through the TPWD (Texas Parks & Wildlife Department) online reservation system. Visit tpwd.texas.gov to confirm current 2026 availability and rates. When booking, be sure to select the correct unit — the main unit on Park Road 31 is where river access, the campground, and picnic areas are located. The separate Bauer Unit is hike-only and does not offer river access or camping.",
  },
  {
    q: "Does Guadalupe River State Park rent tubes, kayaks, or other gear?",
    a: "No — Guadalupe River State Park does not offer any equipment rentals. There are also no tube shuttles operated by the park. Families need to bring their own tubes, kayaks, canoes, PFDs, water shoes, and any other gear they plan to use. Because the park doesn't run a shuttle, most families either float a short segment and walk back along the bank, or arrange a simple car-spot shuttle with a second vehicle. If hauling gear and self-managing logistics sounds like too much hassle, Son's Guadalupe in New Braunfels offers shuttle-assisted tubing along with private cabana rentals and full onsite amenities.",
  },
  {
    q: "What are the rules about pets at Guadalupe River State Park?",
    a: "Pets are allowed at Guadalupe River State Park in most areas, but they must be kept on a leash no longer than 6 feet at all times. It's always worth checking signage on arrival and confirming any updates at tpwd.texas.gov before your trip. The standard Texas State Park rules around alcohol and glass containers also apply regardless of whether you bring a pet.",
  },
  {
    q: "How far is Guadalupe River State Park from San Antonio and Austin?",
    a: "Guadalupe River State Park is located at 3350 Park Road 31 in Spring Branch, TX — a convenient midpoint for Hill Country day-trippers. From San Antonio, the drive is roughly 35–45 minutes via US-281 North. From Austin, expect about 1 hour and 15 minutes via US-290 West and TX-46 South, or via I-35 South to TX-46 West. From New Braunfels, it's only about 25–30 minutes northwest on TX-46 West. Cell service gets thin on Park Road 31, so screenshot your directions before you leave the main highway. Also worth knowing — the commercial Guadalupe tubing operations near New Braunfels are a separate stretch of river, roughly 20 miles downstream from the state park.",
  },
  {
    q: "How is Son's Guadalupe different from Guadalupe River State Park?",
    a: "Son's Guadalupe is a private outfitter on the Guadalupe River in New Braunfels, not part of Guadalupe River State Park and not managed by TPWD. The state park offers a public, wilder river experience with camping, hiking, swimming, and fishing, but no rentals, lifeguards, or tube shuttle. Son's Guadalupe offers a more managed experience with reserved cabanas, shuttle-assisted tubing, in-water picnic tables, hot showers, grills, easy parking, and riverside glamping.",
  },
];

const schemaJson = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: HEAD_TITLE,
    description: DESCRIPTION,
    url: FULL_URL,
    mainEntityOfPage: FULL_URL,
    image: `${SITE}${heroImage}`,
    datePublished: PUBLISH_ISO,
    dateModified: PUBLISH_ISO,
    author: { "@type": "Organization", name: "Son's Guadalupe", url: SITE },
    publisher: {
      "@type": "Organization",
      name: "Son's Guadalupe",
      logo: { "@type": "ImageObject", url: `${SITE}/og-image.png` },
    },
    keywords:
      "guadalupe river state park, Guadalupe River tubing, Guadalupe River swimming, Guadalupe River camping, san marcos river, Rio Vista Park, private Guadalupe River cabanas, Son's Guadalupe",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blog` },
      {
        "@type": "ListItem",
        position: 3,
        name: "Guadalupe River State Park Family Guide",
        item: FULL_URL,
      },
    ],
  },
];

const TOC: { id: string; label: string }[] = [
  { id: "at-a-glance", label: "Guadalupe River State Park at a Glance" },
  { id: "location", label: "Location & Access" },
  { id: "activities", label: "Family Activities" },
  { id: "camping", label: "Camping & Overnight Stays" },
  { id: "rules-safety", label: "Rules & Safety" },
  { id: "fees", label: "Fees, Hours & Reservations" },
  { id: "conditions", label: "River Conditions" },
  { id: "vs-san-marcos", label: "GRSP vs. San Marcos River" },
  { id: "private-alternative", label: "Private Guadalupe River Alternative" },
  { id: "reservations", label: "Booking Park Passes & Son's Guadalupe" },
  { id: "faqs", label: "FAQs" },
];

const ext = (href: string, children: React.ReactNode) => (
  <a href={href} target="_blank" rel="noopener nofollow" className="text-teal underline">
    {children}
  </a>
);

const BlogGuadalupeStateParkFamilyGuide = () => {
  const related = getRelatedPosts(SLUG, 3);

  return (
    <div className="min-h-screen">
      <SEOHead
        title={TITLE}
        description={DESCRIPTION}
        canonical={CANONICAL}
        ogImage={heroImage}
        schemaJson={schemaJson}
      />
      <Helmet>
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={PUBLISH_ISO} />
        <meta property="article:section" content="Guadalupe River Family Guide" />
      </Helmet>
      <Header />

      <main>
        <article className="container mx-auto px-4 md:px-8 pt-28 pb-16 max-w-5xl">
          <BlogBreadcrumb title="Guadalupe River State Park Family Guide" />

          {/* Hero */}
          <header className="mb-8 mt-4">
            <p className="text-sm font-semibold uppercase tracking-wide text-teal mb-3">
              Guadalupe River Family Guide
            </p>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-charcoal mb-4 leading-tight">
              {HEAD_TITLE}
            </h1>
            <p className="text-lg text-charcoal/70 mb-5">
              Everything families need to plan a 2026 trip to Guadalupe River State Park —
              activities, rules, fees, reservations, camping, river conditions, safety, San
              Marcos River comparisons, and a private Guadalupe River alternative.
            </p>
            <p className="flex items-center gap-2 text-sm text-charcoal/60 mb-6">
              <CalendarDays className="w-4 h-4" /> Published {PUBLISH_DATE} · ~14 min read
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/why-sons">
                <Button variant="hero" size="lg">Explore Son's Guadalupe</Button>
              </Link>
              <Link to="/cabanas">
                <Button variant="outline" size="lg">Book a Riverside Cabana</Button>
              </Link>
              <Link to="/glamping">
                <Button variant="ghost" size="lg">View Glamping Stays</Button>
              </Link>
            </div>
          </header>

          <img
            src={heroImage}
            alt={HERO_ALT}
            width={1600}
            height={1067}
            loading="eager"
            decoding="async"
            className="w-full h-auto rounded-2xl shadow-soft mb-10"
          />

          {/* Quick Answer */}
          <aside
            aria-label="Quick answer"
            className="rounded-2xl border-l-4 border-teal bg-teal/5 p-6 mb-10"
          >
            <h2 className="font-heading text-xl font-bold text-charcoal mb-2 flex items-center gap-2">
              <Info className="w-5 h-5 text-teal" /> Quick Answer
            </h2>
            <p className="text-charcoal/80 leading-relaxed">
              Guadalupe River State Park is a family-friendly Texas Hill Country destination
              for swimming, wading, tubing, paddling, fishing, hiking, picnicking, and
              camping. Families should reserve day passes or campsites through TPWD, bring
              their own tubes and life jackets, check current river conditions before
              arriving, and understand that the park has{" "}
              <strong>no rentals, no lifeguards, and no tube shuttle</strong>. For families
              who want a more managed Guadalupe River experience,{" "}
              <Link to="/why-sons" className="text-teal underline">Son's Guadalupe</Link>{" "}
              on River Road in New Braunfels offers reserved cabanas, shuttle-assisted
              tubing, in-water picnic tables, hot showers, grills, and riverside glamping.
            </p>
            <div className="mt-4">
              <Link to="/cabanas">
                <Button variant="hero" size="sm">Plan Your Guadalupe River Day</Button>
              </Link>
            </div>
          </aside>

          {/* TOC */}
          <nav
            aria-label="Table of contents"
            className="rounded-xl border border-charcoal/10 bg-cream/60 p-5 mb-10"
          >
            <details open className="lg:open">
              <summary className="font-heading font-bold text-charcoal cursor-pointer text-lg">
                On this page
              </summary>
              <ul className="mt-3 grid sm:grid-cols-2 gap-y-2 gap-x-6 text-charcoal/80">
                {TOC.map((t) => (
                  <li key={t.id}>
                    <a href={`#${t.id}`} className="text-teal hover:underline">
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </details>
          </nav>

          <div className="prose prose-lg max-w-none text-charcoal/85 prose-headings:font-heading prose-headings:text-charcoal prose-a:text-teal">
            <p className="text-xl leading-relaxed">
              Few places in Texas deliver a genuine "cool off and slow down" moment quite
              like the Guadalupe River. Spring-fed, gin-clear, and framed by bald cypress
              trees that have been shading swimmers for centuries, this stretch of Texas
              Hill Country pulls families out of the Austin and San Antonio heat every
              weekend from May through September — and rewards them with exactly the kind
              of day they were hoping for.
            </p>
            <p>
              If you're planning a 2026 trip, this guide covers everything you need to
              know: the real logistics of Guadalupe River State Park (hours, fees, rules,
              reservations), the best spots for young kids, how river conditions actually
              affect your plans, and an honest comparison with the popular San Marcos
              River options. At the end, you'll also find details on a{" "}
              <Link to="/why-sons">low-crowd, private alternative</Link> on the Guadalupe
              that eliminates most of the headaches families run into at{" "}
              <Link to="/private-vs-public-guadalupe">busy public access points</Link>.
            </p>

            <h2>Welcome to Guadalupe River State Park</h2>
            <p>
              Guadalupe River State Park sits on a particularly beautiful bend of the
              upper Guadalupe, north of New Braunfels in the small community of Spring
              Branch. Unlike the{" "}
              <Link to="/blog/tubing-guadalupe-river-new-braunfels">
                heavily commercialized tubing corridor
              </Link>{" "}
              closer to New Braunfels, the state park feels wilder and quieter — towering
              cypress trees line the banks, white-tailed deer wander the trails at dusk,
              and the river moves at a pace that lets kids actually enjoy it rather than
              just survive it.
            </p>
            <p>
              For families with children under ten, that slower pace is the park's biggest
              selling point. There are no built-up concrete channels, no monster water
              slides, and no bumper-to-bumper tube traffic. What you get is a genuine
              river experience with roughly 4 miles of Guadalupe frontage, limestone
              bluffs, pecan bottomlands, and a campground that puts you to sleep with
              nothing but cicadas and the current.
            </p>
            <p>
              The tradeoff? The park doesn't offer equipment rentals, lifeguards, or the
              shuttle-and-tube infrastructure you'd find at commercial outfitters.
              Families need to bring their own gear, understand the river conditions, and
              manage their own logistics. This guide helps you do exactly that.
            </p>

            {/* Table 1 */}
            <h2 id="at-a-glance">Guadalupe River State Park at a Glance</h2>
            <div className="not-prose overflow-x-auto my-6 rounded-xl border border-charcoal/10">
              <table className="w-full text-sm">
                <thead className="bg-cream">
                  <tr className="text-left">
                    <th className="p-3 font-semibold">Detail</th>
                    <th className="p-3 font-semibold">What Families Should Know</th>
                    <th className="p-3 font-semibold">Planning Tip</th>
                  </tr>
                </thead>
                <tbody className="[&_tr]:border-t [&_tr]:border-charcoal/10">
                  <tr><td className="p-3 font-medium">Location</td><td className="p-3">3350 Park Road 31, Spring Branch, TX 78070</td><td className="p-3">~30 min NW of New Braunfels</td></tr>
                  <tr><td className="p-3 font-medium">Managed by</td><td className="p-3">Texas Parks &amp; Wildlife Department (TPWD)</td><td className="p-3">Official info: tpwd.texas.gov</td></tr>
                  <tr><td className="p-3 font-medium">River frontage</td><td className="p-3">~4 miles of Guadalupe River</td><td className="p-3">Scout entry points before kids run in</td></tr>
                  <tr><td className="p-3 font-medium">Day-use hours</td><td className="p-3">8 a.m. – 10 p.m. (confirm seasonally)</td><td className="p-3">Gates close at capacity on peak days</td></tr>
                  <tr><td className="p-3 font-medium">Entry fees</td><td className="p-3">Per-person day-use fee; confirm 2026 rates with TPWD</td><td className="p-3">Annual Pass pays off after ~3 visits</td></tr>
                  <tr><td className="p-3 font-medium">Reservations</td><td className="p-3">Day-use &amp; campsites via TPWD system</td><td className="p-3">Book holiday weekends 3–4 weeks ahead</td></tr>
                  <tr><td className="p-3 font-medium">Rentals</td><td className="p-3">None — BYO tubes, kayaks, PFDs</td><td className="p-3">Or use a private outfitter downstream</td></tr>
                  <tr><td className="p-3 font-medium">Lifeguards</td><td className="p-3">None — swim at your own risk</td><td className="p-3">USCG-approved PFDs for kids/weak swimmers</td></tr>
                  <tr><td className="p-3 font-medium">Camping</td><td className="p-3">Primitive &amp; developed sites with water/electric</td><td className="p-3">Summer weekends book out fast</td></tr>
                  <tr><td className="p-3 font-medium">Pets</td><td className="p-3">Leashed (6-ft max) in most areas</td><td className="p-3">Keep dogs leashed near current</td></tr>
                  <tr><td className="p-3 font-medium">Alcohol</td><td className="p-3">Prohibited in public areas (state-park rule)</td><td className="p-3">Leave it in the car</td></tr>
                  <tr><td className="p-3 font-medium">Glass</td><td className="p-3">Prohibited throughout the park</td><td className="p-3">Use cans/plastic only</td></tr>
                  <tr><td className="p-3 font-medium">Cell signal</td><td className="p-3">Spotty on Park Road 31</td><td className="p-3">Download offline maps</td></tr>
                </tbody>
              </table>
            </div>

            <h2 id="location">Planning Your Trip: Location &amp; Access</h2>
            <h3>Driving Times from Austin, San Antonio &amp; New Braunfels</h3>
            <ul>
              <li><strong>From San Antonio:</strong> ~35–45 minutes via US-281 North.</li>
              <li><strong>From Austin:</strong> ~1 hr 15 min via US-290 W and TX-46 S, or I-35 S to TX-46 W.</li>
              <li><strong>From New Braunfels:</strong> ~25–30 minutes northwest via TX-46 W toward Spring Branch.</li>
            </ul>
            <p>
              Google Maps is reliable for this route, but cell service gets thin on Park
              Road 31. Screenshot your directions before you leave the highway.
            </p>
            <h3>River Access vs. Bauer Unit</h3>
            <p>
              <strong>Main Park Unit (Park Road 31):</strong> entrance station, campground,
              picnic areas, river swimming, and paddling launch. This is what people mean
              by "Guadalupe River State Park."
            </p>
            <p>
              <strong>Bauer Unit:</strong> a separate, hike-only parcel nearby. No river
              access. Great for birding and quiet hikes, but families coming for river
              time should head to the main unit. When you book via the{" "}
              {ext("https://texasstateparks.reserveamerica.com/", "TPWD reservation system")},
              double-check which unit your pass covers.
            </p>

            <h2 id="activities">Top Family Activities at Guadalupe River State Park</h2>
            <img
              src={inlineRiver}
              alt="Families wading and tubing on a calm Guadalupe River stretch in the Texas Hill Country"
              width={1600}
              height={1067}
              loading="lazy"
              decoding="async"
              className="rounded-xl my-6"
            />
            <h3>Swimming, Wading &amp; Water Play</h3>
            <p>
              The Guadalupe runs at a consistent 68–72°F thanks to spring-fed tributaries
              upstream — on a 100°F afternoon, it feels otherworldly. Several natural
              riverbank access points shallow out over smooth limestone shelves, ideal for
              wading. There are no roped-off swim zones or posted depth markers; scout
              entry points before kids run in. Water shoes are essential.
            </p>
            <p><strong>Key reminder:</strong> There are no lifeguards. Water safety is entirely the family's responsibility.</p>
            <h3>Gentle Tubing &amp; Paddling</h3>
            <p>
              At normal summer flows the river through the park is a slow, scenic float
              with a handful of small riffles. Bring your own tubes, PFDs for any
              non-strong swimmers, and a plan for getting back upstream — the park does
              not operate a shuttle, so families either float a short segment and walk
              back or run a two-car shuttle. Kayaks and canoes are permitted.
            </p>
            <h3>Fishing</h3>
            <p>
              The Guadalupe below Canyon Lake supports Guadalupe bass (the Texas state
              fish), stocked rainbow and brown trout, largemouth bass, and catfish. A
              valid Texas fishing license is required for anyone 17 and older; check TPWD
              for current trout regulations.
            </p>
            <h3>Hiking &amp; Birding</h3>
            <p>
              The main unit has ~8 miles of trails through riparian woodland, cedar/oak
              uplands, and open meadow. The Guadalupe corridor is a major migratory
              flyway — painted buntings, green kingfishers, great blue herons, and the
              endangered golden-cheeked warbler all show up here. Bring binoculars.
            </p>
            <h3>Picnic Areas &amp; Day-Use</h3>
            <p>
              Shaded picnic tables and grills fill up fast on summer weekends — arrive by
              9 a.m. for the best shot at shade. Pack-in/pack-out applies.
            </p>

            <h2 id="camping">Camping &amp; Overnight Stays</h2>
            <ul>
              <li><strong>Developed sites:</strong> Water/electric, picnic table, fire ring, lantern post.</li>
              <li><strong>Primitive sites:</strong> Tent-only, no utilities; some have river views.</li>
              <li><strong>Shared restrooms/showers:</strong> Available in the campground.</li>
            </ul>
            <p>
              Reservations are essentially required for summer weekends and holidays —
              campsites at popular Texas State Parks routinely book out weeks in advance.
              Campfires are allowed in fire rings <em>only</em> when no burn ban is in
              effect — check the {ext("https://tfsweb.tamu.edu/BurnBans/", "Texas A&M Forest Service burn ban map")}{" "}
              before your trip.
            </p>
            <p>
              If your group includes grandparents, toddlers, or anyone who ranks a proper
              shower and real bed above roughing it, you may prefer{" "}
              <Link to="/glamping">riverside glamping at Son's Guadalupe</Link> instead —
              more on that below.
            </p>

            <h2 id="rules-safety">Rules &amp; Safety Tips for Families</h2>

            <div className="not-prose grid md:grid-cols-3 gap-4 my-6">
              <div className="rounded-xl border border-coral/30 bg-coral/5 p-4">
                <p className="flex items-center gap-2 font-bold text-charcoal mb-1">
                  <AlertTriangle className="w-4 h-4 text-coral" /> No Lifeguards
                </p>
                <p className="text-sm text-charcoal/75">
                  Swimming, wading, and tubing are entirely at your own risk. USCG-approved
                  PFDs for kids and weak swimmers.
                </p>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                <p className="flex items-center gap-2 font-bold text-charcoal mb-1">
                  <Info className="w-4 h-4 text-amber-600" /> No Alcohol / No Glass
                </p>
                <p className="text-sm text-charcoal/75">
                  Alcohol is prohibited in public state-park areas. Glass is banned
                  everywhere in the park.
                </p>
              </div>
              <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-4">
                <p className="flex items-center gap-2 font-bold text-charcoal mb-1">
                  <Flame className="w-4 h-4 text-orange-600" /> Burn Ban Check
                </p>
                <p className="text-sm text-charcoal/75">
                  County burn bans override park rules — confirm before lighting any fire.
                </p>
              </div>
            </div>

            <h3>Alcohol, Glass &amp; Container Policies</h3>
            <p>
              <strong>At Guadalupe River State Park:</strong> Alcohol is prohibited in
              public-use areas under Texas Administrative Code. Glass is banned
              throughout the park.
            </p>
            <p>
              <strong>On the commercial Guadalupe stretch near New Braunfels:</strong>{" "}
              Outside TPWD jurisdiction. Cans are generally allowed; glass is always
              prohibited.
            </p>
            <p>
              <strong>At the San Marcos River (Rio Vista / City Park):</strong> San
              Marcos has a city ordinance banning single-use disposable containers
              (including plastic and aluminum) in waterways inside city limits. Bring
              reusable bottles.
            </p>
            <h3>Pet Rules</h3>
            <p>Leash no longer than 6 ft, in trails, picnic areas, and along the river. Clean up after your pet.</p>
            <h3>Fire Restrictions &amp; Grill Usage</h3>
            <p>
              Charcoal in designated grills only; propane usually fine even under partial
              burn bans — confirm with the park. Never build a fire on the riverbank.
            </p>
            <h3>Essential Water Safety</h3>
            <ul>
              <li>Kids under 13 and weak swimmers should wear a properly fitted USCG-approved PFD any time they're near or in the water.</li>
              <li>Scope the entry point yourself before letting kids run in.</li>
              <li>Designate a water-watcher adult who isn't on their phone.</li>
              <li>Exit the river if water suddenly turns murky brown or the current sounds louder — the Guadalupe can rise quickly after upstream rain.</li>
              <li>Avoid flooded or high-water conditions entirely with children.</li>
            </ul>

            <h2 id="fees">Fees, Hours &amp; Reservations for 2026</h2>
            <p>
              Guadalupe River State Park charges a per-person day-use fee. Rather than
              publish a number that may change, confirm current 2026 rates at the{" "}
              {ext("https://tpwd.texas.gov/state-parks/guadalupe-river", "official TPWD park page")}.
              Texas State Parks Annual Pass holders get free day-use entry.
            </p>
            <p>
              Standard day-use hours are 8 a.m. – 10 p.m. The park <strong>closes its
              gates at capacity</strong> on peak summer days — Memorial Day weekend, July
              4th, and Labor Day weekend are the highest-risk days for mid-morning
              closures. Treat reservations as mandatory on holiday weekends.
            </p>
            <h3>Parking Strategies</h3>
            <ul>
              <li>Arrive before 9 a.m. on summer weekends.</li>
              <li>Carpool — fewer vehicles, less stress.</li>
              <li>Ask a ranger about overflow parking on arrival. Never park on private property.</li>
            </ul>
            <h3>What to Bring</h3>
            <div className="not-prose overflow-x-auto my-6 rounded-xl border border-charcoal/10">
              <table className="w-full text-sm">
                <thead className="bg-cream">
                  <tr className="text-left">
                    <th className="p-3 font-semibold">Item</th>
                    <th className="p-3 font-semibold">Why It Matters</th>
                    <th className="p-3 font-semibold">Son's Guadalupe Alternative</th>
                  </tr>
                </thead>
                <tbody className="[&_tr]:border-t [&_tr]:border-charcoal/10">
                  <tr><td className="p-3 font-medium">USCG PFDs / life jackets</td><td className="p-3">No lifeguards, current varies</td><td className="p-3">Same — always recommended for kids</td></tr>
                  <tr><td className="p-3 font-medium">Water shoes</td><td className="p-3">Limestone is slick &amp; sharp</td><td className="p-3">Same — always recommended</td></tr>
                  <tr><td className="p-3 font-medium">Tubes / kayaks</td><td className="p-3">Park has no rentals</td><td className="p-3">Included with shuttle tubing</td></tr>
                  <tr><td className="p-3 font-medium">Pump</td><td className="p-3">Inflate tubes onsite</td><td className="p-3">Not needed — tubes provided</td></tr>
                  <tr><td className="p-3 font-medium">Cooler (no glass)</td><td className="p-3">Glass banned park-wide</td><td className="p-3">Bring your own; grills on-cabana</td></tr>
                  <tr><td className="p-3 font-medium">Trash bags</td><td className="p-3">Pack-in/pack-out</td><td className="p-3">Onsite trash service</td></tr>
                  <tr><td className="p-3 font-medium">Sunscreen + hats</td><td className="p-3">Limited natural shade where you want it</td><td className="p-3">Reserved shaded cabanas</td></tr>
                  <tr><td className="p-3 font-medium">Shade canopy</td><td className="p-3">Tables fill by 9 a.m.</td><td className="p-3">Cabana &amp; in-water picnic shade included</td></tr>
                  <tr><td className="p-3 font-medium">First-aid kit</td><td className="p-3">Remote-feeling park</td><td className="p-3">Staff onsite during operating hours</td></tr>
                  <tr><td className="p-3 font-medium">Offline maps</td><td className="p-3">Spotty cell on Park Rd 31</td><td className="p-3">Good signal on River Road</td></tr>
                  <tr><td className="p-3 font-medium">Fishing gear + TX license</td><td className="p-3">Required for ages 17+</td><td className="p-3">Bring your own</td></tr>
                  <tr><td className="p-3 font-medium">Burn ban check</td><td className="p-3">Bans suspend campfires</td><td className="p-3">Grills permitted at cabana</td></tr>
                </tbody>
              </table>
            </div>

            <h2 id="conditions">Guadalupe River Conditions in 2026</h2>
            <h3>Water Levels &amp; Dam Releases</h3>
            <p>
              The character of the river through the park is shaped by{" "}
              {ext("https://www.swf-wc.usace.army.mil/canyon/", "Canyon Lake releases")}{" "}
              managed by the U.S. Army Corps of Engineers — primarily for flood control,
              not for swimming convenience. The river can shift from a knee-deep wade to a
              swift, turbid flow within hours.
            </p>
            <ul>
              <li><strong>50–150 CFS:</strong> calm wading and easy tubing.</li>
              <li><strong>300–500 CFS+:</strong> meaningful current — close supervision for kids.</li>
              <li><strong>1,000 CFS+:</strong> genuinely hazardous; the park may close river access.</li>
            </ul>
            <h3>Crowds &amp; Quieter Days</h3>
            <p>
              Busiest: Memorial Day, July 4th week, Labor Day, summer Saturdays/Sundays.
              Quietest: weekday mornings, winter weekends. A Thursday or Friday summer
              visit beats the weekend by a significant margin.
            </p>
            <h3>Where to Check Conditions</h3>
            <ul>
              <li>{ext("https://waterdata.usgs.gov/tx/nwis/rt", "USGS National Water Information System")} — current Guadalupe flow.</li>
              <li>{ext("https://tpwd.texas.gov/state-parks/guadalupe-river", "TPWD park page")} — closures &amp; alerts.</li>
              <li>{ext("https://www.swf-wc.usace.army.mil/canyon/", "USACE Canyon Lake")} — release data.</li>
              <li><Link to="/blog/guadalupe-river-water-levels-safety">Our family safety guide</Link> — practical thresholds for kids.</li>
            </ul>

            <h2 id="vs-san-marcos">Guadalupe River State Park vs. San Marcos River</h2>
            <div className="not-prose overflow-x-auto my-6 rounded-xl border border-charcoal/10">
              <table className="w-full text-sm">
                <thead className="bg-cream">
                  <tr className="text-left">
                    <th className="p-3 font-semibold">Factor</th>
                    <th className="p-3 font-semibold">Guadalupe River State Park</th>
                    <th className="p-3 font-semibold">San Marcos (Rio Vista / City Park)</th>
                  </tr>
                </thead>
                <tbody className="[&_tr]:border-t [&_tr]:border-charcoal/10">
                  <tr><td className="p-3 font-medium">Water temperature</td><td className="p-3">~68–72°F</td><td className="p-3">~70–72°F</td></tr>
                  <tr><td className="p-3 font-medium">Water clarity</td><td className="p-3">Excellent (spring-fed)</td><td className="p-3">Excellent (spring-fed)</td></tr>
                  <tr><td className="p-3 font-medium">Crowd level</td><td className="p-3">Moderate–High in peak</td><td className="p-3">Very high on weekends</td></tr>
                  <tr><td className="p-3 font-medium">Natural feel</td><td className="p-3">More remote / wilder</td><td className="p-3">Urban-adjacent</td></tr>
                  <tr><td className="p-3 font-medium">Lifeguards</td><td className="p-3">None</td><td className="p-3">None</td></tr>
                  <tr><td className="p-3 font-medium">Rentals</td><td className="p-3">None</td><td className="p-3">Limited nearby</td></tr>
                  <tr><td className="p-3 font-medium">Container rules</td><td className="p-3">No glass; no alcohol in public areas</td><td className="p-3">Disposable container ban (city ordinance)</td></tr>
                  <tr><td className="p-3 font-medium">Parking</td><td className="p-3">Limited; fills by mid-morning</td><td className="p-3">Tight street &amp; paid lots</td></tr>
                  <tr><td className="p-3 font-medium">Best for</td><td className="p-3">Nature immersion, camping</td><td className="p-3">Easy urban access, social vibe</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              <strong>Rio Vista Park:</strong> playful, lively scene with a small chute
              and kayakers. Confident swimmers and teens love it; toddlers need very
              close supervision near the drop.
            </p>
            <p>
              <strong>San Marcos City Park:</strong> calmer upstream water, remarkably
              transparent. Same{" "}
              {ext("https://www.sanmarcostx.gov/", "City of San Marcos")} disposable
              container ordinance applies — bring reusable bottles.
            </p>

            <h2 id="private-alternative">A Lower-Stress Guadalupe River Option: Son's Guadalupe</h2>
            <img
              src={inlineCabanas}
              alt="Reserved riverside cabanas and shaded gathering areas along the Guadalupe River at Son's Guadalupe in New Braunfels"
              width={1600}
              height={1067}
              loading="lazy"
              decoding="async"
              className="rounded-xl my-6"
            />
            <p>
              For some families, reading through the above — gate closures, no shade
              guarantees, no rentals, no lifeguards, bring-everything logistics — raises a
              reasonable question: <em>is there a way to enjoy the Guadalupe without the
              friction?</em>
            </p>
            <p>
              That's the gap{" "}
              <Link to="/why-sons">Son's Guadalupe</Link> was built to fill. Located on{" "}
              <Link to="/blog/river-road-new-braunfels-guide">River Road in New Braunfels</Link>{" "}
              — on a private stretch of the Guadalupe roughly 20 miles downstream from
              the state park — Son's Guadalupe is a private outfitter (not part of, and
              not managed by, TPWD) with reserved access and a structured, family-first
              experience.
            </p>

            <div className="not-prose rounded-2xl bg-gradient-to-br from-teal/10 to-cream border border-teal/20 p-6 md:p-8 my-8">
              <h3 className="font-heading text-2xl font-bold text-charcoal mb-3">
                What's included at Son's Guadalupe
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-charcoal/85 mb-5">
                <li>✓ Reserved riverside <Link to="/cabanas" className="text-teal underline">cabanas</Link> with shade, grill &amp; power</li>
                <li>✓ In-water covered picnic tables</li>
                <li>✓ <Link to="/tubing" className="text-teal underline">Shuttle-assisted tubing</Link> (2–3 hour float)</li>
                <li>✓ Air-conditioned <Link to="/glamping" className="text-teal underline">bell-tent glamping</Link> with real beds</li>
                <li>✓ Hot showers &amp; clean restrooms</li>
                <li>✓ Easy onsite parking</li>
                <li>✓ Limited daily capacity</li>
                <li>✓ Family-owned, helpful onsite staff</li>
              </ul>
              <p className="text-sm text-charcoal/70 mb-5">
                <strong>Best for:</strong> families and groups who want a reserved,
                lower-stress Guadalupe River experience with cabanas, shuttle-assisted
                tubing, in-water picnic tables, showers, grills, and riverside glamping.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/cabanas"><Button variant="hero">Book a Riverside Cabana</Button></Link>
                <Link to="/glamping"><Button variant="outline">View Glamping Stays</Button></Link>
                <Link to="/why-sons"><Button variant="ghost">Why Son's</Button></Link>
              </div>
            </div>

            <h2 id="reservations">Making Reservations: Park Passes &amp; Son's Guadalupe</h2>
            <h3>Reserving Guadalupe River State Park (TPWD)</h3>
            <ol>
              <li>Visit {ext("https://tpwd.texas.gov/state-parks/guadalupe-river", "the TPWD park page")} and choose Reserve.</li>
              <li>Select day-use pass or campsite.</li>
              <li>Pick date and number of people.</li>
              <li>Pay online and download the Texas State Parks app for your digital pass.</li>
            </ol>
            <p>
              For July 4th and Labor Day, plan 3–4 weeks ahead. The Annual Pass covers
              day-use entry for the pass holder and included guests.
            </p>
            <h3>Booking Son's Guadalupe</h3>
            <p>
              Reservations at Son's Guadalupe are handled directly through{" "}
              <Link to="/book-now">sonsguadalupe.com</Link>. Because capacity is
              intentionally limited, summer weekends and holidays book out early — a few
              weeks ahead is realistic for peak dates.
            </p>

            <h2 id="faqs">Guadalupe River State Park FAQs</h2>
            <Accordion type="single" collapsible className="not-prose">
              {FAQS.map((f, i) => (
                <AccordionItem value={`faq-${i}`} key={i}>
                  <AccordionTrigger className="text-left font-heading text-base md:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-charcoal/80 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <h2>Choosing Your 2026 Guadalupe River Adventure</h2>
            <p>
              <strong>Guadalupe River State Park</strong> is the right choice if you want
              a true state-park experience — camping, hiking, fishing, and a wilder
              stretch of the Guadalupe. Come prepared with your own gear, reserve in
              advance, and check conditions before you leave home.
            </p>
            <p>
              <strong>The San Marcos River options</strong> work well for families who
              want easy urban access with a livelier social vibe. Know the container rules
              and manage parking expectations.
            </p>
            <p>
              <strong>Son's Guadalupe</strong> makes the most sense when your priority is
              a low-stress, guaranteed experience — reserved shaded space, shuttle-assisted
              tubing that returns you to your cabana, hot showers, grills, and the option
              to stay overnight in genuine comfort.
            </p>
            <p className="text-sm italic text-charcoal/60">
              Son's Guadalupe is a family-owned riverside outfitter on River Road in New
              Braunfels, TX. Son's Guadalupe is a private business and is not affiliated
              with or managed by Texas Parks &amp; Wildlife Department or any state park
              system.
            </p>
          </div>

          {/* Final CTA */}
          <section
            aria-label="Plan your Guadalupe River day"
            className="mt-12 rounded-2xl bg-charcoal text-cream p-8 md:p-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3 flex items-center gap-2">
              <Droplets className="w-6 h-6" /> Skip the logistics. Float the Guadalupe.
            </h2>
            <p className="text-cream/85 mb-6 max-w-3xl">
              Want the Guadalupe River without hauling every tube, fighting for shade, or
              figuring out shuttle logistics yourself? Son's Guadalupe gives families and
              groups a reserved riverside home base with cabanas, in-water picnic tables,
              shuttle-assisted tubing, showers, grills, and glamping on River Road in New
              Braunfels.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/why-sons"><Button variant="hero" size="lg">Explore Son's Guadalupe</Button></Link>
              <Link to="/cabanas"><Button variant="outline" size="lg" className="bg-transparent text-cream border-cream hover:bg-cream hover:text-charcoal">Book a Riverside Cabana</Button></Link>
              <Link to="/glamping"><Button variant="outline" size="lg" className="bg-transparent text-cream border-cream hover:bg-cream hover:text-charcoal">View Glamping Stays</Button></Link>
              <Link to="/book-now"><Button variant="ghost" size="lg" className="text-cream hover:bg-cream/10">Plan Your Guadalupe River Day</Button></Link>
            </div>
          </section>
        </article>

        <RelatedReads posts={related} />
      </main>

      <Footer />
    </div>
  );
};

export default BlogGuadalupeStateParkFamilyGuide;
